import { type JSX, useCallback, useEffect, useRef, useState } from "react";

import { BenchApiError, request } from "../api/http";

interface WebhookSignerProps {
  provider: string;
  headerName: string;
  /** The repository the push is for. Its secret is what the signature is computed with. */
  repositoryId: string;
  /** The body that will be sent — the signature covers exactly these bytes. */
  body: unknown;
  /** Whether the bench can find the secret itself, rather than asking for it. */
  canDiscover: boolean;
  onSigned: (headerName: string, headerValue: string) => void;
}

interface SignatureResponse {
  header_name: string;
  header_value: string;
  digest: string;
}

/**
 * Sign a simulated webhook body.
 *
 * The inbound webhook routes are the one part of the API not authenticated with a token: each is
 * verified against a per-repository HMAC secret. Where the bench can read that secret it signs as
 * soon as a repository is chosen, and the secret never leaves the server. Where it cannot, the
 * tester pastes it and it is used once and discarded.
 */
export function WebhookSigner({
  provider,
  headerName,
  repositoryId,
  body,
  canDiscover,
  onSigned,
}: WebhookSignerProps): JSX.Element {
  const [secret, setSecret] = useState("");
  const [state, setState] = useState<"idle" | "signing" | "signed">("idle");
  const [error, setError] = useState<string | null>(null);

  // Signing writes a header into the parent, which re-renders this component with a fresh `body`
  // object and a fresh `onSigned` closure. Neither is a reason to sign again, so the auto-sign
  // effect must not depend on their identities -- it reads them through refs and keys off the
  // request bytes instead. Depending on the callback here is an infinite request loop.
  const latest = useRef({ provider, body, repositoryId, onSigned });
  latest.current = { provider, body, repositoryId, onSigned };

  const signIt = useCallback((withSecret?: string): void => {
    const current = latest.current;
    setState("signing");
    setError(null);
    void request<SignatureResponse>("/api/webhook-signature", {
      method: "POST",
      body: {
        provider: current.provider,
        body: current.body,
        ...(withSecret ? { secret: withSecret } : { repository_id: current.repositoryId }),
      },
    })
      .then((response) => {
        current.onSigned(response.header_name, response.header_value);
        setState("signed");
      })
      .catch((cause: unknown) => {
        setState("idle");
        setError(
          cause instanceof BenchApiError ? cause.message : `Could not sign: ${String(cause)}`,
        );
      });
  }, []);

  // Re-sign whenever the repository or the body changes: the signature covers the body byte for
  // byte, and the secret is per repository, so either change invalidates it.
  const bodyKey = JSON.stringify(body ?? null);
  useEffect(() => {
    if (!canDiscover || !repositoryId) {
      setState("idle");
      return;
    }
    // Editing the body re-signs, so settle first rather than signing once per keystroke.
    const timer = setTimeout(signIt, 250);
    return () => clearTimeout(timer);
  }, [canDiscover, provider, repositoryId, bodyKey, signIt]);

  if (canDiscover) {
    return (
      <section className="signer">
        {!repositoryId && (
          <p className="muted">
            Choose a repository above and <code>{headerName}</code> is signed automatically.
          </p>
        )}

        {repositoryId && state === "signing" && <p className="muted">Signing…</p>}

        {repositoryId && state === "signed" && (
          <p className="muted">
            <code>{headerName}</code> signed with this repository&rsquo;s secret, which stays on
            the server. It is recomputed whenever the body or the repository changes.
          </p>
        )}

        {error && (
          <div className="notice notice--error">
            <strong>Could not sign that.</strong> {error}
            <p className="muted">
              Paste the secret below instead, or check that this environment&rsquo;s graph is
              reachable.
            </p>
            <ManualSecret secret={secret} setSecret={setSecret} onSign={signIt} headerName={headerName} />
          </div>
        )}
      </section>
    );
  }

  return (
    <section className="signer">
      <ManualSecret secret={secret} setSecret={setSecret} onSign={signIt} headerName={headerName} />

      {state === "signed" && (
        <p className="muted">
          Signed. <code>{headerName}</code> is filled in below — the signature covers the body
          exactly as it stands, so re-sign after any edit.
        </p>
      )}

      {error && (
        <div className="notice notice--error">
          <strong>Could not sign that.</strong> {error}
        </div>
      )}

      <p className="muted">
        This environment cannot look the secret up. Read it from the graph:{" "}
        <code>MATCH (r:Repository) RETURN r.id, r.hmac_secret</code>, or configure{" "}
        <code>neo4j_uri</code> and <code>neo4j_password</code> on the environment to have it signed
        for you.
      </p>
    </section>
  );
}

function ManualSecret({
  secret,
  setSecret,
  onSign,
  headerName,
}: {
  secret: string;
  setSecret: (value: string) => void;
  onSign: (secret: string) => void;
  headerName: string;
}): JSX.Element {
  return (
    <label className="signer__field">
      <span>Repository HMAC secret</span>
      <span className="input-row">
        <input
          type="password"
          value={secret}
          autoComplete="off"
          placeholder="the secret the service stored for this repository"
          onChange={(event) => setSecret(event.target.value)}
        />
        <button type="button" onClick={() => onSign(secret)} disabled={!secret.trim()}>
          Sign and fill {headerName}
        </button>
      </span>
    </label>
  );
}
